import {useEffect, useCallback, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import {EVENTS, track} from 'src/common/helpers'
import {useGetTrackers} from 'src/common/hooks/useGetTrackers'
import {DREAM_DATA_SERVICE_ID, DREAM_DATA_SERVICE_NAME} from 'src/common/constants'
import {getServiceConsent} from 'src/common/helpers/consent'
import {UserCentricsEvent} from '../types'

export const OnRouteChangeTracker = (): null => {
  const {getActiveTracker, loading, error} = useGetTrackers()
  const router = useRouter()
  const debounceRef = useRef<NodeJS.Timeout>()
  const prevLoadingRef = useRef(loading)

  // Track consent state reactively
  const [hasConsent, setHasConsent] = useState(() => getServiceConsent(DREAM_DATA_SERVICE_ID))

  // Listen for consent changes
  useEffect(() => {
    const checkConsent = (event: Event) => {
      const detail = (event as CustomEvent<UserCentricsEvent>).detail
      const currentConsent = detail?.[DREAM_DATA_SERVICE_NAME]
      setHasConsent(currentConsent)
    }

    // Listen for Usercentrics consent updates (if available)
    window.addEventListener('ucEvent', checkConsent as EventListener)

    return () => {
      window.removeEventListener('ucEvent', checkConsent as EventListener)
    }
  }, [])

  // Log tracker initialization errors
  useEffect(() => {
    if (error) {
      console.error('DreamData tracker initialization failed:', error)
    }
  }, [error])

  const trackPageEvent = useCallback(
    () => {
      if (loading || error || typeof window === 'undefined') {
        return
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // Debounce rapid route changes
      debounceRef.current = setTimeout(() => {
        const currentUrl = window.location.pathname || router.asPath

        // Skip tracking for slice-simulator route
        if (currentUrl.startsWith('/slice-simulator')) {
          return
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [DEV] Would track page load event:', {
            url: currentUrl,
            hasConsent,
            loading,
            events: {
              dreamdata: hasConsent ? 'cookie tracker' : 'cookieless tracker',
              gtm: `${EVENTS.PAGE_LOAD} with location: ${currentUrl}`,
            },
          })

          return
        }

        try {
          // Get the appropriate tracker based on current consent
          const activeTracker = getActiveTracker(hasConsent)

          if (activeTracker) {
            console.log(`Tracking page load with ${hasConsent ? 'cookie' : 'cookieless'} tracker`)
            activeTracker.page({path: currentUrl})
          } else {
            console.warn('No active tracker available for tracking')
          }

          track({
            event: EVENTS.PAGE_LOAD,
            location: currentUrl,
          })
        } catch (getTrackerError) {
          console.error('Error tracking page event:', getTrackerError)
        }
      }, 100)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router.asPath is intentionally omitted to prevent callback recreation on every route change.
    [getActiveTracker, loading, error, hasConsent],
  )

  // Track initial page load and route changes
  useEffect(() => {
    // Track initial page load when component mounts
    trackPageEvent()

    // Handler for route changes
    const handleRouteChange = () => {
      trackPageEvent()
    }

    // Set up listeners for different route change events
    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)

      // Clean up any pending debounced calls
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [router.events, trackPageEvent])

  // Retry tracking when trackers finish loading
  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    prevLoadingRef.current = loading

    // If trackers just finished loading, retry tracking the current page
    if (wasLoading && !loading) {
      trackPageEvent()
    }
  }, [loading, trackPageEvent])

  return null
}
